import { OrderCard } from 'src/components/Orders/OrderCards/orderCard';
import { Order } from 'src/utils/interfaces/interfaces';
import { useNavigate } from 'react-router-dom';
import {
  useGetApprovalOrdersListMutation,
  useGetOrdersListByCompanyMutation,
  useGetOrdersListMutation,
  useLazyGetApprovalOrderByStatusQuery,
} from 'src/services/orders/orders';
import { Input } from 'src/components/UI/Forms/Inputs';
import { useState, useEffect, useRef } from 'react';
import { APPROVED, READY } from 'src/constants/statusConstants';
import { useTranslation } from 'react-i18next';
import {
  Pagination,
  Tabs,
  Tab,
  Chip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalContent,
  ModalFooter,
  Button,
} from '@heroui/react';
import i18n from 'src/i18n';
import OrdersTable from 'src/pages/orders/ordersListView';
import ReviewOrder from './reviewOrder';
import { TbReportSearch } from 'react-icons/tb';
import Reports from '../reports/reports';

export interface IOrdersProps { }

type OrderType = 'completed' | 'ongoing' | 'pending' | 'rejected' | 'approved';
interface Meta {
  count: number;
  limit: number;
  page: number;
}

interface OrdersState {
  completed: Order[];
  ongoing: Order[];
  draft: Order[];
  pending: Order[];
  rejected: Order[];
  approved: Order[];
}

interface MetaState {
  completed: Meta;
  ongoing: Meta;
  pending: Meta;
  rejected: Meta;
  approved: Meta;
}

export function ApprovalOrders() {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [orders, setOrders] = useState<OrdersState>({
    completed: [],
    ongoing: [],
    draft: [],
    pending: [],
    rejected: [],
    approved: [],
  });

  const [meta, setMeta] = useState<MetaState>({
    completed: { count: 0, limit: 20, page: 1 },
    ongoing: { count: 0, limit: 20, page: 1 },
    pending: { count: 0, limit: 20, page: 1 },
    rejected: { count: 0, limit: 20, page: 1 },
    approved: { count: 0, limit: 20, page: 1 },
  });

  const [isListView, setIsListView] = useState(() => {
    const savedIsListView = window.localStorage.getItem('isListView');
    return savedIsListView !== null ? JSON.parse(savedIsListView) : true;
  });

  const [type, setTypeView] = useState<OrderType>(() => {
    const savedType = window.localStorage.getItem('activeTab') as OrderType;
    return savedType && ['completed', 'ongoing', 'pending'].includes(savedType)
      ? savedType
      : 'pending';
  });

  const [activeOrder, setActiveOrder] = useState<string | null>(() => {
    return window.localStorage.getItem('activeOrder');
  });

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>(() => {
    return window.localStorage.getItem('searchTerm') || '';
  });

  const [getOrdersList] = useGetOrdersListMutation();
  const [getApprovalOrdersList, refetch] = useGetApprovalOrdersListMutation();
  const [getApprovalOrderByStatus] = useLazyGetApprovalOrderByStatusQuery();
  // const [searchOrders] = useSearchOrdersMutation();

  const containerRef = useRef<HTMLDivElement>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);

  const statusMapping: Record<OrderType, string> = {
    pending: 'pending_approval',
    approved: 'approved',
    rejected: 'rejected',
    ongoing: 'in_transit',
    completed: 'completed',
  };
  // -----------------------
  // fetchOrders function
  // -----------------------
  const fetchOrders = async (
    orderType: OrderType,
    page: number,
    search: string = '',
    entity?: string
  ) => {
    setIsOrderLoading(true);
    try {
      const localMeta = meta[orderType];

      if (orderType === 'approved') {
        const params = {
          limit: meta.approved.limit,
          page,
          ...(entity ? { entity } : {}),
        };

        const [approvedRes, pendingPaymentRes] = await Promise.all([
          getApprovalOrderByStatus('approved').unwrap(),
          getApprovalOrderByStatus('pending_payment').unwrap(),
        ]);

        const readyRes = await getOrdersList({
          status: READY,
          ...params,
        }).unwrap();

        const approvedOrders = approvedRes?.response ?? [];
        const pendingPaymentOrders = pendingPaymentRes?.response ?? [];
        const readyOrders = readyRes?.response ?? [];

        const combinedOrders = [
          ...approvedOrders,
          ...pendingPaymentOrders,
          ...readyOrders,
        ];

        const totalCount =
          (approvedRes?.meta?.count ?? 0) +
          (pendingPaymentRes?.meta?.count ?? 0) +
          (readyRes?.meta?.count ?? 0);

        setOrders((prev) => ({
          ...prev,
          approved: combinedOrders,
        }));

        setMeta((prevMeta) => ({
          ...prevMeta,
          approved: {
            ...prevMeta.approved,
            count: totalCount,
            page,
          },
        }));

        return;
      }

      // Add explicit handling for 'ongoing' orders
      if (orderType === 'ongoing') {
        const res = await getOrdersList({
          status: 'in_transit',
          limit: localMeta.limit,
          page,
          ...(entity && { entity }),
        }).unwrap();

        const ongoingOrders = res?.response ?? [];
        const ongoingCount = res?.meta?.count ?? 0;

        setOrders((prev) => ({
          ...prev,
          ongoing: ongoingOrders,
        }));

        setMeta((prevMeta) => ({
          ...prevMeta,
          ongoing: {
            ...prevMeta.ongoing,
            count: ongoingCount,
            page,
          },
        }));

        return;
      }

      // fallback for other statuses
      const apiStatus = statusMapping[orderType];
      const res = await getApprovalOrdersList({
        status: apiStatus,
        limit: localMeta.limit,
        page,
        ...(entity && { entity }),
      }).unwrap();

      const ordersData = res.response || [];
      const ordersCount = res.meta?.count || 0;

      setOrders((prev) => ({
        ...prev,
        [orderType]: ordersData,
      }));

      setMeta((prevMeta) => ({
        ...prevMeta,
        [orderType]: {
          ...prevMeta[orderType],
          count: ordersCount,
          page,
        },
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsOrderLoading(false);
    }
  };

  // -----------------------
  // fetchSearchOrders function
  // -----------------------
  // const fetchSearchOrders = async (
  //   orderType: OrderType,
  //   search: string,
  //   entity?: string
  // ) => {
  //   setIsOrderLoading(true);
  //   try {
  //     if (orderType === "other") {
  //       // Two calls for APPROVED & READY
  //       const params: any = { word: search };
  //       if (entity) {
  //         params.entity = entity;
  //       }

  //       const approvedSearchRes = await searchOrders({
  //         status: APPROVED,
  //         ...params,
  //       }).unwrap();
  //       const readySearchRes = await searchOrders({
  //         status: READY,
  //         ...params,
  //       }).unwrap();

  //       // Combine
  //       const combinedResults = [
  //         ...(approvedSearchRes || []),
  //         ...(readySearchRes || []),
  //       ];

  //       setOrders((prev) => ({
  //         ...prev,
  //         other: combinedResults,
  //       }));
  //     } else {
  //       // Single status for ongoing or completed
  //       const status =
  //         orderType === "ongoing"
  //           ? IN_TRANSIT
  //           : orderType === "completed"
  //           ? COMPLETED
  //           : APPROVED;

  //       const params: any = { status, word: search };
  //       if (entity) {
  //         params.entity = entity;
  //       }

  //       const response = await searchOrders(params).unwrap();
  //       if (response && response.length > 0) {
  //         setOrders((prevData) => ({
  //           ...prevData,
  //           [orderType]: response,
  //         }));
  //       } else {
  //         setOrders((prevData) => ({
  //           ...prevData,
  //           [orderType]: [],
  //         }));
  //       }
  //     }
  //   } catch (error) {
  //   } finally {
  //     setIsOrderLoading(false);
  //   }
  // };

  useEffect(() => {
    const fromOrdersPage = sessionStorage.getItem('fromOrdersPage');

    if (fromOrdersPage) {
      const savedPage = window.localStorage.getItem('activePage');
      if (meta[type]) {
        if (searchTerm) {
          // fetchSearchOrders(type, searchTerm);
        } else {
          fetchOrders(type, savedPage ? Number(savedPage) : meta[type].page);
        }
      }
      sessionStorage.removeItem('fromOrdersPage');
    } else {
      fetchOrders(type, meta[type].page);
    }
  }, []);

  const onChangeSearch = (searchString: string) => {
    // setSearchTerm(searchString);
    // if (searchString) {
    //   window.localStorage.setItem("searchTerm", searchString);
    //   fetchSearchOrders(
    //     type,
    //     searchString,
    //     selectedEntity !== "all" ? selectedEntity : undefined
    //   );
    // } else {
    //   window.localStorage.removeItem("searchTerm");
    //   fetchOrders(
    //     type,
    //     meta[type].page,
    //     "",
    //     selectedEntity !== "all" ? selectedEntity : undefined
    //   );
    // }
  };

  const ListView = () => {
    window.localStorage.removeItem('activeOrder');
    setActiveOrder(null);
    setIsListView((prevIsListView: boolean) => {
      const newIsListView = !prevIsListView;
      window.localStorage.setItem('isListView', JSON.stringify(newIsListView));
      return newIsListView;
    });
  };

  const onTabChange = (newType: string | number) => {
    setTypeView(newType as OrderType);
    setMeta((prev) => ({
      ...prev,
      [newType as OrderType]: {
        ...prev[newType as OrderType],
        page: 1,
      },
    }));
    if (searchTerm) {
      // fetchSearchOrders(
      //   newType as OrderType,
      //   searchTerm,
      //   selectedEntity !== "all" ? selectedEntity : undefined
      // );
    } else {
      fetchOrders(
        newType as OrderType,
        1,
        '',
        selectedEntity !== 'all' ? selectedEntity : undefined
      );
    }
  };

  const handlePageClick = (newPage: number) => {
    setMeta((prev) => ({
      ...prev,
      [type]: { ...prev[type], page: newPage },
    }));
    if (searchTerm) {
      // fetchSearchOrders(
      //   type,
      //   searchTerm,
      //   selectedEntity !== "all" ? selectedEntity : undefined
      // );
    } else {
      fetchOrders(
        type,
        newPage,
        '',
        selectedEntity !== 'all' ? selectedEntity : undefined
      );
    }
  };

  const totalPageCount = Math.ceil(meta[type].count / meta[type].limit);

  const handleOrderClick = (order: Order) => {
    setIsReviewOpen(true);
    setActiveOrder(order._id);
    setSelectedOrder(order);
  };


  const handleOngoingOrderClick = (order: Order) => {

    sessionStorage.setItem('fromOrdersPage', 'true');
    window.localStorage.setItem('activeTab', type);
    window.localStorage.setItem('activeOrder', order._id);
    window.localStorage.setItem('activePage', meta[type].page.toString());
    window.localStorage.setItem(
      'scrollPosition',
      containerRef.current?.scrollTop.toString() || '0'
    );
    setActiveOrder(order._id);
    navigate(`/orders/${order._id}`);
  };

  return (
    <div className="flex w-full mt-2 px-6">
      <div
        className="w-full h-screen px-6 overflow-y-auto pt-4" style={{ backgroundColor: "var(--bg-base)", color: "var(--text-main)" }}
        ref={containerRef}
      >
        {/* Sticky Header and Tabs */}
        <div className="sticky top-0 z-40" style={{ backgroundColor: "var(--bg-base)" }}>
          {/* Header */}
          <div className="pt-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-3xl">{t('orders_approval.title')}</h3>
              </div>
              <div>
                <div className="flex justify-end mt-4">
                  <Input
                    type="text"
                    placeholder={t('orders_approval.search_textbox')}
                    inputClassName="bg-[var(--bg-surface-2)] border border-[var(--border-light)] shadow-sm rounded-lg placeholder:text-[var(--text-muted)] text-[var(--text-main)] h-[45px]"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onChangeSearch(e.target.value)
                    }
                    iconUrl="/assets/icons/search.svg"
                    defaultValue={searchTerm}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tabs with icons and counts */}
          <div className="flex items-center w-full flex-row border-b border-[var(--border-light)]">
            <Tabs
              aria-label="Orders Tabs"
              variant="underlined"
              selectedKey={type}
              onSelectionChange={(key) => onTabChange(key)}
              classNames={{
                tabList:
                  'gap-10 w-full relative rounded-none p-0 overflow-hidden',
                cursor: 'w-full bg-[var(--color-primary)]',
                tab: 'max-w-fit px-1 h-14',
                tabContent:
                  'text-white/50 group-data-[selected=true]:text-white transition-all duration-300',
              }}
            >
              <Tab
                key="pending"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                        ? 'space-x-reverse space-x-2'
                        : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      {t('orders_approval.pending_orders')}
                    </span>
                    {type === 'pending' &&
                      !searchTerm &&
                      meta.pending.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="bg-[var(--bg-surface-2)] border border-[var(--border-light)] text-[var(--text-main)] text-md shadow-lg"
                        >
                          {meta.pending.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>
              <Tab
                key="approved"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                        ? 'space-x-reverse space-x-2'
                        : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      Approved orders
                    </span>
                    {type === 'approved' &&
                      !searchTerm &&
                      meta.approved.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="bg-[var(--bg-surface-2)] border border-[var(--border-light)] text-[var(--text-main)] text-md shadow-lg"
                        >
                          {meta.approved.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>
              <Tab
                key="ongoing"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                        ? 'space-x-reverse space-x-2'
                        : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      Ongoing orders
                    </span>
                    {type === 'ongoing' &&
                      !searchTerm &&
                      meta.ongoing.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="bg-[var(--bg-surface-2)] border border-[var(--border-light)] text-[var(--text-main)] text-md shadow-lg"
                        >
                          {meta.ongoing.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>
              <Tab
                key="rejected"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                        ? 'space-x-reverse space-x-2'
                        : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      Rejected Orders
                    </span>
                    {type === 'rejected' &&
                      !searchTerm &&
                      meta.rejected.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="bg-[var(--bg-surface-2)] border border-[var(--border-light)] text-[var(--text-main)] text-md shadow-lg"
                        >
                          {meta.rejected.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>
            </Tabs>

            <div className="flex gap-4 mb-4">
              <Button
                variant="solid"
                className="rounded-md p-2 shadow-lg transition-colors"
                style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
                onPress={() => setIsReportOpen(true)}
                startContent={<TbReportSearch className="w-5 h-5" />}
              >
                {t('orders_page.report')}
              </Button>
            </div>

            {/* <div className="flex gap-2 mb-4">
              <Button
                variant="solid"
                isIconOnly
                // onPress={ListView}
                className=" bg-bluePrimary-100/[0.9] rounded-md p-0"
              >
                {isListView ? (
                  <GridViewIcon
                    className="text-white"
                    titleAccess="Card View"
                    fontSize="small"
                  />
                ) : (
                  <ListIcon
                    className="text-white"
                    titleAccess="List View"
                    fontSize="small"
                  />
                )}
              </Button>
            </div> */}
          </div>
        </div>

        {/* Content Area */}
        <div>
          {/* "Ready" Orders Tab (key="other") */}
          {type === 'pending' && (
            <div className="transition-all duration-300">
              {orders.pending && orders.pending.length > 0 ? (
                isListView ? (
                  <div className="mt-6">
                    <OrdersTable
                      orders={orders.pending}
                      type="pending_approval"
                      onRowClick={handleOrderClick}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.pending.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                        onClick={() => {
                          // Only allow click if status is APPROVED
                          // You can customize this check to also allow READY
                          if (
                            order.status === APPROVED ||
                            order.status === READY
                          ) {
                            handleOrderClick(order);
                          }
                        }}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6">
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center">
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {type === 'approved' && (
            <div className="transition-all duration-300">
              {orders.approved && orders.approved.length > 0 ? (
                isListView ? (
                  <div className="mt-6">
                    <OrdersTable
                      orders={orders.approved}
                      type="approved"
                      onRowClick={handleOrderClick}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.approved.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                        onClick={() => {
                          // Only allow click if status is APPROVED
                          // You can customize this check to also allow READY
                          if (
                            order.status === APPROVED ||
                            order.status === READY
                          ) {
                            handleOrderClick(order);
                          }
                        }}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6">
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center">
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {type === 'ongoing' && (
            <div className="transition-all duration-300">
              {orders.ongoing && orders.ongoing.length > 0 ? (
                isListView ? (
                  <div className="mt-6">
                    <OrdersTable
                      orders={orders.ongoing}
                      type="ongoing"
                      onRowClick={(order) => {
                        handleOngoingOrderClick(order)
                      }}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.ongoing.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                        onClick={() => {
                          // Only allow click if status is APPROVED
                          // You can customize this check to also allow READY
                          if (
                            order.status === APPROVED ||
                            order.status === READY
                          ) {
                            handleOngoingOrderClick(order)
                          }
                        }}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6">
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center">
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {type === 'rejected' && (
            <div className="transition-all duration-300">
              {orders.rejected && orders.rejected.length > 0 ? (
                isListView ? (
                  <div className="mt-6">
                    <OrdersTable
                      orders={orders.rejected}
                      type="rejected"
                      onRowClick={handleOrderClick}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.rejected.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                      // onClick={() => {
                      //   // Only allow click if status is APPROVED
                      //   // You can customize this check to also allow READY
                      //   if (
                      //     order.status === APPROVED ||
                      //     order.status === READY
                      //   ) {
                      //     handleOrderClick(order);
                      //   }
                      // }}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6">
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center">
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {!searchTerm && orders[type].length > 0 && (
            <div
              className={`fixed bottom-0 w-full flex justify-center p-4 z-10`} style={{ backgroundColor: "var(--bg-base)", borderTop: "1px solid var(--border-light)" }}
            >
              <Pagination
                variant="flat"
                key={type}
                color="danger"
                size={'md'}
                showShadow={false}
                showControls
                total={totalPageCount}
                initialPage={1}
                page={meta[type].page}
                onChange={(newPage) => handlePageClick(newPage)}
                isDisabled={isOrderLoading}
                classNames={{
                  next: `bg-white/[0.06] [&[data-hover=true]:not([data-active=true])]:bg-white/[0.08] text-white ${i18n.language === 'ar' ? 'scale-x-[-1]' : 'scale-x-1'
                    }`,
                  prev: `bg-white/[0.06] [&[data-hover=true]:not([data-active=true])]:bg-white/[0.08] text-white ${i18n.language === 'ar' ? 'scale-x-[-1]' : 'scale-x-1'
                    }`,
                  item: 'bg-white/[0.04] m-1 shadow-lg text-white [&[data-hover=true]:not([data-active=true])]:bg-white/[0.08]',
                }}
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        size="5xl"
        isOpen={isReviewOpen}
        onClose={() => setIsReviewOpen(false)}
        className="p-0 w-[85vw] h-[80vh] max-w-none border shadow-card" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-light)", color: "var(--text-main)" }}
        backdrop="blur"
        classNames={{
          closeButton:
            'bg-white/[0.06] m-1 hover:bg-white/[0.09] active:bg-white/[0.06] text-white p-2',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-white font-bold text-2xl mt-1 ps-8">
                Order Review
              </ModalHeader>
              <ModalBody className="overflow-y-auto">
                <ReviewOrder
                  orderId={activeOrder}
                  onCloseModal={() => setIsReviewOpen(false)}
                  onActionComplete={() => {
                    fetchOrders(type, meta[type].page);
                  }}
                />
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        size="full"
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        className="p-0 border shadow-card" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-light)", color: "var(--text-main)" }}
        backdrop="blur"
        classNames={{
          closeButton:
            'bg-white/[0.06] m-1 hover:bg-white/[0.09] active:bg-white/[0.06] text-white p-2',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-white font-bold text-2xl mt-1 ps-8">
                {t('orders_page.report')}
              </ModalHeader>
              <ModalBody className="overflow-y-auto">
                <Reports />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}

export default ApprovalOrders;
